import type { LangMap } from "./index";

export const tools: LangMap = {
  it: {
    meta: {
      title: "Podcast & Mappe — Lumen",
      description:
        "Trasforma qualsiasi argomento in un podcast di studio o in una mappa mentale. Con guida integrata su come costruire mappe efficaci.",
    },
    page: {
      kicker: "Strumenti",
      title: "Podcast & Mappe",
      subtitle:
        "Ascolta i tuoi argomenti come un podcast o visualizzali come mappe. Impara più velocemente attivando più sensi.",
    },
    tabs: { podcast: "Podcast", maps: "Mappe", guide: "Guida" },
    podcast: {
      card: {
        title: "Genera un podcast di studio",
        desc: "Una conversazione tra due voci che spiega l'argomento come se stessi ascoltando la radio.",
      },
      topicLabel: "Argomento",
      topicPlaceholder: "Es. La rivoluzione francese, Il ciclo di Krebs, Le equazioni differenziali…",
      levelLabel: "Livello",
      level: { base: "Base", medio: "Medio", avanzato: "Avanzato" },
      durationLabel: "Durata: {{min}} min",
      sourceLabel: "Materiale di partenza (opzionale)",
      sourcePlaceholder: "Incolla appunti, oppure allega un audio (lezione registrata) o una foto (pagina del libro).",
      generateButton: "Genera podcast",
      generatingButton: "Sto scrivendo il podcast…",
      toastReady: "Podcast pronto ✨",
      historyTitle: "I tuoi podcast",
      historyBadge: "{{min}}′ · {{level}}",
      voices: {
        alloy: { label: "Alloy", tone: "Neutra, versatile" },
        nova: { label: "Nova", tone: "Femminile, calda" },
        shimmer: { label: "Shimmer", tone: "Femminile, chiara" },
        coral: { label: "Coral", tone: "Femminile, espressiva" },
        sage: { label: "Sage", tone: "Femminile, pacata" },
        fable: { label: "Fable", tone: "Narrativa, britannica" },
        onyx: { label: "Onyx", tone: "Maschile, profonda" },
        echo: { label: "Echo", tone: "Maschile, calma" },
        ash: { label: "Ash", tone: "Maschile, giovane" },
        ballad: { label: "Ballad", tone: "Maschile, morbida" },
        verse: { label: "Verse", tone: "Maschile, espressiva" },
      },
      player: {
        tracksBadge: "{{count}} tracce",
        voiceALabel: "Voce A (host)",
        voiceBLabel: "Voce B (esperto)",
        speedLabel: "Velocità: {{speed}}×",
        listen: "Ascolta",
        generatingVoice: "Sto generando la voce…",
        resume: "Riprendi",
        pause: "Pausa",
        stopAria: "Stop",
        chaptersLabel: "Capitoli",
        chapterIntro: "Introduzione",
        chapterOutro: "Conclusione",
        chapterPart: "Parte {{n}}",
        hostLabel: "Host",
        expertLabel: "Esperto",
        albumLabel: "Lumen · Podcast",
        ttsInstructions: "Parla in italiano con un tono naturale, caldo e coinvolgente, come un vero podcaster.",
      },
    },
    errors: {
      cannotReadFile: "Impossibile leggere il file",
      noMapToEdit: "Nessuna mappa da modificare",
      textTooLarge: "Il file di testo è troppo grande (max 5MB)",
      audioTooLarge: "Il file audio è troppo grande (max 20MB)",
      imageTooLarge: "L'immagine è troppo grande (max 10MB)",
      missingTitleCenter: "Aggiungi titolo e idea centrale",
    },
    map: {
      tabs: { ai: "Genera con IA", manual: "Crea manuale", library: "Le mie mappe" },
      library: { title: "Le mie mappe", itemLabel: "mappa" },
      ai: {
        title: "Genera una mappa con l'IA",
        desc: "L'IA analizza l'argomento e costruisce automaticamente una mappa completa e gerarchica.",
        sourceLabel: "Fonte",
        source: { topic: "Argomento", text: "Testo", audio: "Audio", image: "Foto" },
        text: {
          placeholder: "Incolla qui i tuoi appunti, un capitolo, un riassunto…",
          uploadFile: "Carica file .txt / .md",
          charsCount: "{{count}}/20000",
        },
        audio: {
          transcribing: "Trascrizione in corso…",
          uploadLabel: "Carica una registrazione",
          formats: "MP3, WAV, M4A, WEBM · max 20MB",
        },
        image: {
          reading: "Sto leggendo l'immagine…",
          uploadLabel: "Carica una foto",
          formats: "Appunti, pagina di libro, lavagna · max 10MB",
        },
        fromLabel: "Da: {{name}}",
        topicLabel: "Argomento",
        mapTitleLabel: "Titolo mappa",
        topicPlaceholder: "Es. La seconda guerra mondiale",
        mapTitlePlaceholder: "Es. Appunti di storia",
        typeLabel: "Tipo di mappa",
        kind: {
          mind: "Mind map (radiale)",
          concept: "Mappa concettuale (Novak)",
          cornell: "Schema Cornell",
          dsa: "Mappa DSA per verifica",
        },
        dsaNote:
          "Mappa pensata per studenti con DSA: etichette corte, simboli visivi, solo l'essenziale per la verifica, con una domanda tipo per ogni ramo.",
        customRequestLabel: "Richieste specifiche",
        optional: "(opzionale)",
        customRequestPlaceholderDsa: "Es. focalizzati sui capitoli 3 e 4, evidenzia le date, usa colori per i personaggi",
        customRequestPlaceholderDefault: "Es. aggiungi esempi pratici, semplifica il linguaggio",
        generateButton: "Genera mappa",
        generatingButton: "Sto costruendo la mappa…",
        dsaBadge: "DSA · verifica",
        editRequestButton: "Modifica con richiesta",
        refine: {
          title: "Modifica la mappa",
          desc: "Scrivi cosa vuoi cambiare: l'IA aggiorna la mappa mantenendo la struttura esistente.",
          placeholder: "Es. aggiungi un ramo sulle conseguenze, accorcia le etichette, metti più date, togli il ramo sui personaggi",
          defaultPrompt: "Migliora chiarezza e semplicità.",
          updateButton: "Aggiorna mappa",
          updatingButton: "Sto aggiornando…",
          suggestions: {
            simplify: "Semplifica il linguaggio",
            addDates: "Aggiungi più date/numeri",
            shorterLabels: "Etichette più corte",
            moreVisualSymbols: "Più simboli visivi",
            essentialOnly: "Solo l'essenziale per la verifica",
          },
        },
        toasts: {
          mapGenerated: "Mappa generata ✨",
          mapUpdated: "Mappa aggiornata ✨",
          textUploaded: "Testo caricato",
          audioTranscribed: "Audio trascritto ✨",
          imageExtracted: "Testo estratto dall'immagine ✨",
        },
      },
      manual: {
        titleLabel: "Titolo mappa",
        titlePlaceholder: "Es. Ripasso storia",
        centerLabel: "Idea centrale",
        centerPlaceholder: "Es. Rivoluzione Francese",
        defaultCenter: "Idea centrale",
        branchPlaceholder: "Ramo principale",
        removeBranchAria: "Rimuovi ramo",
        subPlaceholder: "Sotto-argomento {{n}}",
        addSubButton: "Aggiungi",
        addBranchButton: "Aggiungi ramo",
        saveButton: "Salva mappa",
        savingButton: "Salvataggio…",
        previewLabel: "Anteprima",
        toastSaved: "Mappa salvata ✨",
      },
    },
    guide: {
      whyTitle: "Perché fare mappe?",
      whenLabel: "Quando usarla:",
      whyDesc:
        "Le mappe traducono il testo in immagini. Il cervello ricorda l'80% di ciò che vede e organizza spazialmente, contro il 20% di ciò che legge in modo lineare. Sono lo strumento #1 per il ripasso attivo.",
      mindMap: {
        kind: "Mind Map",
        pillars: [
          "1 idea centrale al centro (parola + immagine).",
          "3-6 rami principali che partono a raggio.",
          "Ogni ramo si apre in sotto-rami più sottili.",
          "Un colore diverso per ogni ramo (aiuta la memoria).",
          "Parole chiave, non frasi lunghe.",
        ],
        when: "Per brainstorming, ripasso creativo, argomenti aperti (temi, saggi, storia).",
        example: "ESEMPIO — \"Rivoluzione francese\"\n\n            Cause\n              |\nSocietà — RIVOLUZIONE — Fasi\n              |\n           Conseguenze",
      },
      conceptMap: {
        kind: "Mappa concettuale (Novak)",
        pillars: [
          "Concetti in riquadri, gerarchia dall'alto in basso.",
          "Frecce che collegano concetti con una etichetta di relazione (es. 'causa', 'è formato da').",
          "Va letta come frasi: soggetto → verbo → oggetto.",
          "Adatta a spiegare processi e relazioni causa-effetto.",
        ],
        when: "Per materie scientifiche, tecniche, o quando devi mostrare le relazioni tra concetti.",
        example: "[Piante]\n   |è formata da\n[Fusto] — [Foglie] — [Radici]\n              |producono\n         [Fotosintesi]",
      },
      cornell: {
        kind: "Schema Cornell",
        pillars: [
          "Dividi la pagina in 3 zone.",
          "Colonna destra (larga): appunti presi in lezione.",
          "Colonna sinistra (stretta): parole chiave e domande.",
          "Fascia in fondo: riassunto scritto a memoria dopo la lezione.",
        ],
        when: "Durante lezioni frontali, videocorsi, letture di manuali.",
        example: "| Parole    | Appunti…                        |\n| chiave    | Appunti…                        |\n| domande?  | Appunti…                        |\n|-----------|---------------------------------|\n| Riassunto: 3 righe a memoria a fine lezione. |",
      },
      method: {
        title: "Metodo in 5 passi",
        steps: [
          { title: "Leggi attivamente", desc: "Prima leggi tutto il capitolo senza scrivere nulla. Sottolinea solo le parole chiave." },
          { title: "Trova l'idea centrale", desc: "Riassumila in 1-3 parole. È il cuore della mappa." },
          { title: "Identifica i rami", desc: "Cerca 3-6 macro-temi che rispondono a \"di cosa parla?\"." },
          { title: "Aggiungi dettagli", desc: "Per ogni ramo scrivi 2-4 sotto-argomenti — solo parole chiave." },
          { title: "Ripassa a occhi chiusi", desc: "Chiudi la mappa e prova a rifarla a mente. Se manca qualcosa, torna al ramo mancante." },
        ],
      },
    },
  },
  en: {
    meta: {
      title: "Podcasts & Maps — Lumen",
      description:
        "Turn any topic into a study podcast or a mind map. With a built-in guide on how to build effective maps.",
    },
    page: {
      kicker: "Tools",
      title: "Podcasts & Maps",
      subtitle:
        "Listen to your topics like a podcast or visualize them as maps. Learn faster by engaging more senses.",
    },
    tabs: { podcast: "Podcast", maps: "Maps", guide: "Guide" },
    podcast: {
      card: {
        title: "Generate a study podcast",
        desc: "A conversation between two voices explaining the topic as if you were listening to the radio.",
      },
      topicLabel: "Topic",
      topicPlaceholder: "E.g. The French Revolution, The Krebs cycle, Differential equations…",
      levelLabel: "Level",
      level: { base: "Basic", medio: "Medium", avanzato: "Advanced" },
      durationLabel: "Duration: {{min}} min",
      sourceLabel: "Source material (optional)",
      sourcePlaceholder: "Paste notes, or attach an audio (recorded lecture) or a photo (book page).",
      generateButton: "Generate podcast",
      generatingButton: "Writing the podcast…",
      toastReady: "Podcast ready ✨",
      historyTitle: "Your podcasts",
      historyBadge: "{{min}}′ · {{level}}",
      voices: {
        alloy: { label: "Alloy", tone: "Neutral, versatile" },
        nova: { label: "Nova", tone: "Female, warm" },
        shimmer: { label: "Shimmer", tone: "Female, bright" },
        coral: { label: "Coral", tone: "Female, expressive" },
        sage: { label: "Sage", tone: "Female, calm" },
        fable: { label: "Fable", tone: "Narrative, British" },
        onyx: { label: "Onyx", tone: "Male, deep" },
        echo: { label: "Echo", tone: "Male, calm" },
        ash: { label: "Ash", tone: "Male, young" },
        ballad: { label: "Ballad", tone: "Male, soft" },
        verse: { label: "Verse", tone: "Male, expressive" },
      },
      player: {
        tracksBadge: "{{count}} tracks",
        voiceALabel: "Voice A (host)",
        voiceBLabel: "Voice B (expert)",
        speedLabel: "Speed: {{speed}}×",
        listen: "Listen",
        generatingVoice: "Generating voice…",
        resume: "Resume",
        pause: "Pause",
        stopAria: "Stop",
        chaptersLabel: "Chapters",
        chapterIntro: "Introduction",
        chapterOutro: "Conclusion",
        chapterPart: "Part {{n}}",
        hostLabel: "Host",
        expertLabel: "Expert",
        albumLabel: "Lumen · Podcast",
        ttsInstructions: "Speak in English with a natural, warm and engaging tone, like a real podcaster.",
      },
    },
    errors: {
      cannotReadFile: "Unable to read the file",
      noMapToEdit: "No map to edit",
      textTooLarge: "The text file is too large (max 5MB)",
      audioTooLarge: "The audio file is too large (max 20MB)",
      imageTooLarge: "The image is too large (max 10MB)",
      missingTitleCenter: "Add a title and a central idea",
    },
    map: {
      tabs: { ai: "Generate with AI", manual: "Create manually", library: "My maps" },
      library: { title: "My maps", itemLabel: "map" },
      ai: {
        title: "Generate a map with AI",
        desc: "AI analyzes the topic and automatically builds a complete, hierarchical map.",
        sourceLabel: "Source",
        source: { topic: "Topic", text: "Text", audio: "Audio", image: "Photo" },
        text: {
          placeholder: "Paste your notes, a chapter, a summary here…",
          uploadFile: "Upload .txt / .md file",
          charsCount: "{{count}}/20000",
        },
        audio: {
          transcribing: "Transcribing…",
          uploadLabel: "Upload a recording",
          formats: "MP3, WAV, M4A, WEBM · max 20MB",
        },
        image: {
          reading: "Reading the image…",
          uploadLabel: "Upload a photo",
          formats: "Notes, book page, whiteboard · max 10MB",
        },
        fromLabel: "From: {{name}}",
        topicLabel: "Topic",
        mapTitleLabel: "Map title",
        topicPlaceholder: "E.g. World War II",
        mapTitlePlaceholder: "E.g. History notes",
        typeLabel: "Map type",
        kind: {
          mind: "Mind map (radial)",
          concept: "Concept map (Novak)",
          cornell: "Cornell notes",
          dsa: "Accessible exam map",
        },
        dsaNote:
          "Map designed for students with learning differences: short labels, visual symbols, only the essentials for the exam, with a sample question for each branch.",
        customRequestLabel: "Specific requests",
        optional: "(optional)",
        customRequestPlaceholderDsa: "E.g. focus on chapters 3 and 4, highlight dates, use colors for characters",
        customRequestPlaceholderDefault: "E.g. add practical examples, simplify the language",
        generateButton: "Generate map",
        generatingButton: "Building the map…",
        dsaBadge: "Accessible · exam",
        editRequestButton: "Edit with a request",
        refine: {
          title: "Edit the map",
          desc: "Write what you want to change: the AI updates the map while keeping its existing structure.",
          placeholder: "E.g. add a branch about the consequences, shorten the labels, add more dates, remove the branch about characters",
          defaultPrompt: "Improve clarity and simplicity.",
          updateButton: "Update map",
          updatingButton: "Updating…",
          suggestions: {
            simplify: "Simplify the language",
            addDates: "Add more dates/numbers",
            shorterLabels: "Shorter labels",
            moreVisualSymbols: "More visual symbols",
            essentialOnly: "Only the essentials for the exam",
          },
        },
        toasts: {
          mapGenerated: "Map generated ✨",
          mapUpdated: "Map updated ✨",
          textUploaded: "Text uploaded",
          audioTranscribed: "Audio transcribed ✨",
          imageExtracted: "Text extracted from image ✨",
        },
      },
      manual: {
        titleLabel: "Map title",
        titlePlaceholder: "E.g. History review",
        centerLabel: "Central idea",
        centerPlaceholder: "E.g. French Revolution",
        defaultCenter: "Central idea",
        branchPlaceholder: "Main branch",
        removeBranchAria: "Remove branch",
        subPlaceholder: "Sub-topic {{n}}",
        addSubButton: "Add",
        addBranchButton: "Add branch",
        saveButton: "Save map",
        savingButton: "Saving…",
        previewLabel: "Preview",
        toastSaved: "Map saved ✨",
      },
    },
    guide: {
      whyTitle: "Why make maps?",
      whenLabel: "When to use it:",
      whyDesc:
        "Maps translate text into images. The brain remembers 80% of what it sees and organizes spatially, versus 20% of what it reads linearly. They're the #1 tool for active review.",
      mindMap: {
        kind: "Mind Map",
        pillars: [
          "1 central idea in the middle (word + image).",
          "3-6 main branches radiating outward.",
          "Each branch opens into thinner sub-branches.",
          "A different color for each branch (helps memory).",
          "Keywords, not long sentences.",
        ],
        when: "For brainstorming, creative review, open topics (essays, history, themes).",
        example: "EXAMPLE — \"French Revolution\"\n\n            Causes\n              |\nSociety — REVOLUTION — Phases\n              |\n         Consequences",
      },
      conceptMap: {
        kind: "Concept map (Novak)",
        pillars: [
          "Concepts in boxes, hierarchy top to bottom.",
          "Arrows linking concepts with a relation label (e.g. 'causes', 'is made of').",
          "Should read like sentences: subject → verb → object.",
          "Good for explaining processes and cause-effect relationships.",
        ],
        when: "For scientific, technical subjects, or when you need to show relationships between concepts.",
        example: "[Plants]\n   |is made of\n[Stem] — [Leaves] — [Roots]\n              |produce\n         [Photosynthesis]",
      },
      cornell: {
        kind: "Cornell notes",
        pillars: [
          "Divide the page into 3 zones.",
          "Right column (wide): notes taken in class.",
          "Left column (narrow): keywords and questions.",
          "Bottom strip: summary written from memory after class.",
        ],
        when: "During lectures, video courses, textbook readings.",
        example: "| Words     | Notes…                          |\n| keywords  | Notes…                          |\n| questions?| Notes…                          |\n|-----------|---------------------------------|\n| Summary: 3 lines from memory after class. |",
      },
      method: {
        title: "5-step method",
        steps: [
          { title: "Read actively", desc: "First read the whole chapter without writing anything. Underline only keywords." },
          { title: "Find the central idea", desc: "Sum it up in 1-3 words. It's the heart of the map." },
          { title: "Identify the branches", desc: "Look for 3-6 macro-themes that answer \"what is it about?\"." },
          { title: "Add details", desc: "For each branch write 2-4 sub-topics — keywords only." },
          { title: "Review with your eyes closed", desc: "Close the map and try to redo it from memory. If something's missing, go back to that branch." },
        ],
      },
    },
  },
  es: {
    meta: {
      title: "Podcasts y Mapas — Lumen",
      description: "Convierte cualquier tema en un podcast de estudio o en un mapa mental. Con guía integrada sobre cómo construir mapas eficaces.",
    },
    page: { kicker: "Herramientas", title: "Podcasts y Mapas", subtitle: "Escucha tus temas como un podcast o visualízalos como mapas. Aprende más rápido activando más sentidos." },
    tabs: { podcast: "Podcast", maps: "Mapas", guide: "Guía" },
    podcast: {
      card: { title: "Genera un podcast de estudio", desc: "Una conversación entre dos voces que explica el tema como si escucharas la radio." },
      topicLabel: "Tema",
      topicPlaceholder: "Ej. La Revolución Francesa, El ciclo de Krebs, Las ecuaciones diferenciales…",
      levelLabel: "Nivel",
      level: { base: "Básico", medio: "Medio", avanzato: "Avanzado" },
      durationLabel: "Duración: {{min}} min",
      sourceLabel: "Material de partida (opcional)",
      sourcePlaceholder: "Pega apuntes, o adjunta un audio (clase grabada) o una foto (página de un libro).",
      generateButton: "Generar podcast",
      generatingButton: "Escribiendo el podcast…",
      toastReady: "Podcast listo ✨",
      historyTitle: "Tus podcasts",
      historyBadge: "{{min}}′ · {{level}}",
      voices: {
        alloy: { label: "Alloy", tone: "Neutra, versátil" },
        nova: { label: "Nova", tone: "Femenina, cálida" },
        shimmer: { label: "Shimmer", tone: "Femenina, clara" },
        coral: { label: "Coral", tone: "Femenina, expresiva" },
        sage: { label: "Sage", tone: "Femenina, tranquila" },
        fable: { label: "Fable", tone: "Narrativa, británica" },
        onyx: { label: "Onyx", tone: "Masculina, profunda" },
        echo: { label: "Echo", tone: "Masculina, calmada" },
        ash: { label: "Ash", tone: "Masculina, joven" },
        ballad: { label: "Ballad", tone: "Masculina, suave" },
        verse: { label: "Verse", tone: "Masculina, expresiva" },
      },
      player: {
        tracksBadge: "{{count}} pistas",
        voiceALabel: "Voz A (presentador)",
        voiceBLabel: "Voz B (experto)",
        speedLabel: "Velocidad: {{speed}}×",
        listen: "Escuchar",
        generatingVoice: "Generando la voz…",
        resume: "Reanudar",
        pause: "Pausa",
        stopAria: "Detener",
        chaptersLabel: "Capítulos",
        chapterIntro: "Introducción",
        chapterOutro: "Conclusión",
        chapterPart: "Parte {{n}}",
        hostLabel: "Presentador",
        expertLabel: "Experto",
        albumLabel: "Lumen · Podcast",
        ttsInstructions: "Habla en español con un tono natural, cálido y cautivador, como un verdadero podcaster.",
      },
    },
    errors: {
      cannotReadFile: "No se puede leer el archivo",
      noMapToEdit: "No hay ningún mapa para editar",
      textTooLarge: "El archivo de texto es demasiado grande (máx. 5MB)",
      audioTooLarge: "El archivo de audio es demasiado grande (máx. 20MB)",
      imageTooLarge: "La imagen es demasiado grande (máx. 10MB)",
      missingTitleCenter: "Añade un título y una idea central",
    },
    map: {
      tabs: { ai: "Generar con IA", manual: "Crear manualmente", library: "Mis mapas" },
      library: { title: "Mis mapas", itemLabel: "mapa" },
      ai: {
        title: "Genera un mapa con IA",
        desc: "La IA analiza el tema y construye automáticamente un mapa completo y jerárquico.",
        sourceLabel: "Fuente",
        source: { topic: "Tema", text: "Texto", audio: "Audio", image: "Foto" },
        text: { placeholder: "Pega aquí tus apuntes, un capítulo, un resumen…", uploadFile: "Subir archivo .txt / .md", charsCount: "{{count}}/20000" },
        audio: { transcribing: "Transcribiendo…", uploadLabel: "Sube una grabación", formats: "MP3, WAV, M4A, WEBM · máx. 20MB" },
        image: { reading: "Leyendo la imagen…", uploadLabel: "Sube una foto", formats: "Apuntes, página de libro, pizarra · máx. 10MB" },
        fromLabel: "De: {{name}}",
        topicLabel: "Tema",
        mapTitleLabel: "Título del mapa",
        topicPlaceholder: "Ej. La Segunda Guerra Mundial",
        mapTitlePlaceholder: "Ej. Apuntes de historia",
        typeLabel: "Tipo de mapa",
        kind: { mind: "Mapa mental (radial)", concept: "Mapa conceptual (Novak)", cornell: "Esquema Cornell", dsa: "Mapa accesible para examen" },
        dsaNote: "Mapa pensado para estudiantes con dificultades de aprendizaje: etiquetas cortas, símbolos visuales, solo lo esencial para el examen, con una pregunta tipo por cada rama.",
        customRequestLabel: "Solicitudes específicas",
        optional: "(opcional)",
        customRequestPlaceholderDsa: "Ej. céntrate en los capítulos 3 y 4, resalta las fechas, usa colores para los personajes",
        customRequestPlaceholderDefault: "Ej. añade ejemplos prácticos, simplifica el lenguaje",
        generateButton: "Generar mapa",
        generatingButton: "Construyendo el mapa…",
        dsaBadge: "Accesible · examen",
        editRequestButton: "Editar con una solicitud",
        refine: {
          title: "Editar el mapa",
          desc: "Escribe lo que quieres cambiar: la IA actualiza el mapa manteniendo la estructura existente.",
          placeholder: "Ej. añade una rama sobre las consecuencias, acorta las etiquetas, añade más fechas, quita la rama sobre los personajes",
          defaultPrompt: "Mejora la claridad y la simplicidad.",
          updateButton: "Actualizar mapa",
          updatingButton: "Actualizando…",
          suggestions: { simplify: "Simplifica el lenguaje", addDates: "Añade más fechas/números", shorterLabels: "Etiquetas más cortas", moreVisualSymbols: "Más símbolos visuales", essentialOnly: "Solo lo esencial para el examen" },
        },
        toasts: { mapGenerated: "Mapa generado ✨", mapUpdated: "Mapa actualizado ✨", textUploaded: "Texto subido", audioTranscribed: "Audio transcrito ✨", imageExtracted: "Texto extraído de la imagen ✨" },
      },
      manual: {
        titleLabel: "Título del mapa",
        titlePlaceholder: "Ej. Repaso de historia",
        centerLabel: "Idea central",
        centerPlaceholder: "Ej. Revolución Francesa",
        defaultCenter: "Idea central",
        branchPlaceholder: "Rama principal",
        removeBranchAria: "Eliminar rama",
        subPlaceholder: "Subtema {{n}}",
        addSubButton: "Añadir",
        addBranchButton: "Añadir rama",
        saveButton: "Guardar mapa",
        savingButton: "Guardando…",
        previewLabel: "Vista previa",
        toastSaved: "Mapa guardado ✨",
      },
    },
    guide: {
      whyTitle: "¿Por qué hacer mapas?",
      whenLabel: "Cuándo usarlo:",
      whyDesc: "Los mapas traducen el texto en imágenes. El cerebro recuerda el 80% de lo que ve y organiza espacialmente, frente al 20% de lo que lee de forma lineal. Son la herramienta n.º1 para el repaso activo.",
      mindMap: {
        kind: "Mapa mental",
        pillars: ["1 idea central en el medio (palabra + imagen).", "3-6 ramas principales que salen en radio.", "Cada rama se abre en sub-ramas más finas.", "Un color distinto para cada rama (ayuda a la memoria).", "Palabras clave, no frases largas."],
        when: "Para lluvia de ideas, repaso creativo, temas abiertos (ensayos, historia, temas).",
        example: "EJEMPLO — \"Revolución francesa\"\n\n            Causas\n              |\nSociedad — REVOLUCIÓN — Fases\n              |\n         Consecuencias",
      },
      conceptMap: {
        kind: "Mapa conceptual (Novak)",
        pillars: ["Conceptos en recuadros, jerarquía de arriba a abajo.", "Flechas que conectan conceptos con una etiqueta de relación (ej. 'causa', 'está formado por').", "Se lee como frases: sujeto → verbo → objeto.", "Adecuado para explicar procesos y relaciones causa-efecto."],
        when: "Para materias científicas, técnicas, o cuando debas mostrar relaciones entre conceptos.",
        example: "[Plantas]\n   |está formada por\n[Tallo] — [Hojas] — [Raíces]\n              |producen\n         [Fotosíntesis]",
      },
      cornell: {
        kind: "Esquema Cornell",
        pillars: ["Divide la página en 3 zonas.", "Columna derecha (ancha): apuntes tomados en clase.", "Columna izquierda (estrecha): palabras clave y preguntas.", "Franja inferior: resumen escrito de memoria después de la clase."],
        when: "Durante clases magistrales, videocursos, lecturas de manuales.",
        example: "| Palabras  | Apuntes…                        |\n| clave     | Apuntes…                        |\n| preguntas?| Apuntes…                        |\n|-----------|---------------------------------|\n| Resumen: 3 líneas de memoria al final de la clase. |",
      },
      method: {
        title: "Método en 5 pasos",
        steps: [
          { title: "Lee activamente", desc: "Primero lee todo el capítulo sin escribir nada. Subraya solo las palabras clave." },
          { title: "Encuentra la idea central", desc: "Resúmela en 1-3 palabras. Es el corazón del mapa." },
          { title: "Identifica las ramas", desc: "Busca 3-6 macrotemas que respondan a \"¿de qué trata?\"." },
          { title: "Añade detalles", desc: "Para cada rama escribe 2-4 subtemas — solo palabras clave." },
          { title: "Repasa con los ojos cerrados", desc: "Cierra el mapa e intenta rehacerlo de memoria. Si falta algo, vuelve a esa rama." },
        ],
      },
    },
  },
  fr: {
    meta: {
      title: "Podcasts & Cartes — Lumen",
      description:
        "Transforme n'importe quel sujet en podcast d'étude ou en carte mentale. Avec un guide intégré pour créer des cartes efficaces.",
    },
    page: {
      kicker: "Outils",
      title: "Podcasts & Cartes",
      subtitle:
        "Écoute tes sujets comme un podcast ou visualise-les sous forme de cartes. Apprends plus vite en sollicitant plusieurs sens.",
    },
    tabs: { podcast: "Podcast", maps: "Cartes", guide: "Guide" },
    podcast: {
      card: {
        title: "Génère un podcast d'étude",
        desc: "Une conversation entre deux voix qui explique le sujet comme si tu écoutais la radio.",
      },
      topicLabel: "Sujet",
      topicPlaceholder: "Ex. La Révolution française, Le cycle de Krebs, Les équations différentielles…",
      levelLabel: "Niveau",
      level: { base: "Débutant", medio: "Intermédiaire", avanzato: "Avancé" },
      durationLabel: "Durée : {{min}} min",
      sourceLabel: "Support de départ (facultatif)",
      sourcePlaceholder: "Colle des notes, ou joins un audio (cours enregistré) ou une photo (page d'un livre).",
      generateButton: "Générer le podcast",
      generatingButton: "Rédaction du podcast…",
      toastReady: "Podcast prêt ✨",
      historyTitle: "Tes podcasts",
      historyBadge: "{{min}}′ · {{level}}",
      voices: {
        alloy: { label: "Alloy", tone: "Neutre, polyvalente" },
        nova: { label: "Nova", tone: "Féminine, chaleureuse" },
        shimmer: { label: "Shimmer", tone: "Féminine, claire" },
        coral: { label: "Coral", tone: "Féminine, expressive" },
        sage: { label: "Sage", tone: "Féminine, posée" },
        fable: { label: "Fable", tone: "Narrative, britannique" },
        onyx: { label: "Onyx", tone: "Masculine, grave" },
        echo: { label: "Echo", tone: "Masculine, calme" },
        ash: { label: "Ash", tone: "Masculine, jeune" },
        ballad: { label: "Ballad", tone: "Masculine, douce" },
        verse: { label: "Verse", tone: "Masculine, expressive" },
      },
      player: {
        tracksBadge: "{{count}} pistes",
        voiceALabel: "Voix A (animateur)",
        voiceBLabel: "Voix B (expert)",
        speedLabel: "Vitesse : {{speed}}×",
        listen: "Écouter",
        generatingVoice: "Génération de la voix…",
        resume: "Reprendre",
        pause: "Pause",
        stopAria: "Stop",
        chaptersLabel: "Chapitres",
        chapterIntro: "Introduction",
        chapterOutro: "Conclusion",
        chapterPart: "Partie {{n}}",
        hostLabel: "Animateur",
        expertLabel: "Expert",
        albumLabel: "Lumen · Podcast",
        ttsInstructions: "Parle en français avec un ton naturel, chaleureux et captivant, comme un vrai podcasteur.",
      },
    },
    errors: {
      cannotReadFile: "Impossible de lire le fichier",
      noMapToEdit: "Aucune carte à modifier",
      textTooLarge: "Le fichier texte est trop volumineux (max 5 Mo)",
      audioTooLarge: "Le fichier audio est trop volumineux (max 20 Mo)",
      imageTooLarge: "L'image est trop volumineuse (max 10 Mo)",
      missingTitleCenter: "Ajoute un titre et une idée centrale",
    },
    map: {
      tabs: { ai: "Générer avec l'IA", manual: "Créer manuellement", library: "Mes cartes" },
      library: { title: "Mes cartes", itemLabel: "carte" },
      ai: {
        title: "Génère une carte avec l'IA",
        desc: "L'IA analyse le sujet et construit automatiquement une carte complète et hiérarchisée.",
        sourceLabel: "Source",
        source: { topic: "Sujet", text: "Texte", audio: "Audio", image: "Photo" },
        text: {
          placeholder: "Colle ici tes notes, un chapitre, un résumé…",
          uploadFile: "Charger un fichier .txt / .md",
          charsCount: "{{count}}/20000",
        },
        audio: {
          transcribing: "Transcription en cours…",
          uploadLabel: "Charger un enregistrement",
          formats: "MP3, WAV, M4A, WEBM · max 20 Mo",
        },
        image: {
          reading: "Lecture de l'image…",
          uploadLabel: "Charger une photo",
          formats: "Notes, page de livre, tableau · max 10 Mo",
        },
        fromLabel: "De : {{name}}",
        topicLabel: "Sujet",
        mapTitleLabel: "Titre de la carte",
        topicPlaceholder: "Ex. La Seconde Guerre mondiale",
        mapTitlePlaceholder: "Ex. Notes d'histoire",
        typeLabel: "Type de carte",
        kind: {
          mind: "Carte mentale (radiale)",
          concept: "Carte conceptuelle (Novak)",
          cornell: "Schéma Cornell",
          dsa: "Carte accessible pour l'examen",
        },
        dsaNote:
          "Carte conçue pour les élèves à besoins spécifiques : étiquettes courtes, symboles visuels, seulement l'essentiel pour l'examen, avec une question type par branche.",
        customRequestLabel: "Demandes spécifiques",
        optional: "(facultatif)",
        customRequestPlaceholderDsa: "Ex. concentre-toi sur les chapitres 3 et 4, surligne les dates, utilise des couleurs pour les personnages",
        customRequestPlaceholderDefault: "Ex. ajoute des exemples pratiques, simplifie le langage",
        generateButton: "Générer la carte",
        generatingButton: "Construction de la carte…",
        dsaBadge: "Accessible · examen",
        editRequestButton: "Modifier avec une demande",
        refine: {
          title: "Modifier la carte",
          desc: "Écris ce que tu veux changer : l'IA met à jour la carte en conservant sa structure existante.",
          placeholder: "Ex. ajoute une branche sur les conséquences, raccourcis les étiquettes, ajoute plus de dates, supprime la branche sur les personnages",
          defaultPrompt: "Améliore la clarté et la simplicité.",
          updateButton: "Mettre à jour la carte",
          updatingButton: "Mise à jour…",
          suggestions: {
            simplify: "Simplifier le langage",
            addDates: "Ajouter plus de dates/chiffres",
            shorterLabels: "Étiquettes plus courtes",
            moreVisualSymbols: "Plus de symboles visuels",
            essentialOnly: "Seulement l'essentiel pour l'examen",
          },
        },
        toasts: {
          mapGenerated: "Carte générée ✨",
          mapUpdated: "Carte mise à jour ✨",
          textUploaded: "Texte chargé",
          audioTranscribed: "Audio transcrit ✨",
          imageExtracted: "Texte extrait de l'image ✨",
        },
      },
      manual: {
        titleLabel: "Titre de la carte",
        titlePlaceholder: "Ex. Révision d'histoire",
        centerLabel: "Idée centrale",
        centerPlaceholder: "Ex. Révolution française",
        defaultCenter: "Idée centrale",
        branchPlaceholder: "Branche principale",
        removeBranchAria: "Supprimer la branche",
        subPlaceholder: "Sous-thème {{n}}",
        addSubButton: "Ajouter",
        addBranchButton: "Ajouter une branche",
        saveButton: "Enregistrer la carte",
        savingButton: "Enregistrement…",
        previewLabel: "Aperçu",
        toastSaved: "Carte enregistrée ✨",
      },
    },
    guide: {
      whyTitle: "Pourquoi faire des cartes ?",
      whenLabel: "Quand l'utiliser :",
      whyDesc:
        "Les cartes traduisent le texte en images. Le cerveau retient 80% de ce qu'il voit et organise spatialement, contre 20% de ce qu'il lit de façon linéaire. C'est l'outil n°1 pour la révision active.",
      mindMap: {
        kind: "Carte mentale",
        pillars: [
          "1 idée centrale au milieu (mot + image).",
          "3-6 branches principales qui rayonnent.",
          "Chaque branche s'ouvre en sous-branches plus fines.",
          "Une couleur différente pour chaque branche (aide la mémoire).",
          "Des mots-clés, pas des phrases longues.",
        ],
        when: "Pour le brainstorming, la révision créative, les sujets ouverts (dissertations, histoire, thèmes).",
        example: "EXEMPLE — \"Révolution française\"\n\n            Causes\n              |\nSociété — RÉVOLUTION — Phases\n              |\n         Conséquences",
      },
      conceptMap: {
        kind: "Carte conceptuelle (Novak)",
        pillars: [
          "Concepts dans des cadres, hiérarchie de haut en bas.",
          "Flèches reliant les concepts avec une étiquette de relation (ex. 'cause', 'est composé de').",
          "Se lit comme des phrases : sujet → verbe → objet.",
          "Adaptée pour expliquer des processus et des relations de cause à effet.",
        ],
        when: "Pour les matières scientifiques, techniques, ou quand tu dois montrer des relations entre concepts.",
        example: "[Plantes]\n   |est composée de\n[Tige] — [Feuilles] — [Racines]\n              |produisent\n         [Photosynthèse]",
      },
      cornell: {
        kind: "Schéma Cornell",
        pillars: [
          "Divise la page en 3 zones.",
          "Colonne droite (large) : notes prises en cours.",
          "Colonne gauche (étroite) : mots-clés et questions.",
          "Bande du bas : résumé écrit de mémoire après le cours.",
        ],
        when: "Pendant les cours magistraux, les vidéocours, les lectures de manuels.",
        example: "| Mots      | Notes…                          |\n| clés      | Notes…                          |\n| questions?| Notes…                          |\n|-----------|---------------------------------|\n| Résumé : 3 lignes de mémoire en fin de cours. |",
      },
      method: {
        title: "Méthode en 5 étapes",
        steps: [
          { title: "Lis activement", desc: "Lis d'abord tout le chapitre sans rien écrire. Souligne seulement les mots-clés." },
          { title: "Trouve l'idée centrale", desc: "Résume-la en 1-3 mots. C'est le cœur de la carte." },
          { title: "Identifie les branches", desc: "Cherche 3-6 macro-thèmes qui répondent à \"de quoi ça parle ?\"." },
          { title: "Ajoute des détails", desc: "Pour chaque branche, écris 2-4 sous-thèmes — seulement des mots-clés." },
          { title: "Révise les yeux fermés", desc: "Ferme la carte et essaie de la refaire de mémoire. S'il manque quelque chose, retourne à cette branche." },
        ],
      },
    },
  },
  de: {
    meta: {
      title: "Podcasts & Mindmaps — Lumen",
      description:
        "Verwandle jedes Thema in einen Lern-Podcast oder eine Mindmap. Mit integriertem Leitfaden für effektive Mindmaps.",
    },
    page: {
      kicker: "Werkzeuge",
      title: "Podcasts & Mindmaps",
      subtitle:
        "Höre dir deine Themen wie einen Podcast an oder stelle sie als Mindmap dar. Lerne schneller, indem du mehrere Sinne aktivierst.",
    },
    tabs: { podcast: "Podcast", maps: "Mindmaps", guide: "Leitfaden" },
    podcast: {
      card: {
        title: "Erstelle einen Lern-Podcast",
        desc: "Ein Gespräch zwischen zwei Stimmen, das das Thema erklärt, als würdest du Radio hören.",
      },
      topicLabel: "Thema",
      topicPlaceholder: "Z. B. Die Französische Revolution, Der Zitronensäurezyklus, Differentialgleichungen…",
      levelLabel: "Niveau",
      level: { base: "Grundlagen", medio: "Mittel", avanzato: "Fortgeschritten" },
      durationLabel: "Dauer: {{min}} Min.",
      sourceLabel: "Ausgangsmaterial (optional)",
      sourcePlaceholder: "Füge Notizen ein oder hänge eine Audiodatei (aufgezeichnete Stunde) oder ein Foto (Buchseite) an.",
      generateButton: "Podcast erstellen",
      generatingButton: "Podcast wird geschrieben…",
      toastReady: "Podcast bereit ✨",
      historyTitle: "Deine Podcasts",
      historyBadge: "{{min}}′ · {{level}}",
      voices: {
        alloy: { label: "Alloy", tone: "Neutral, vielseitig" },
        nova: { label: "Nova", tone: "Weiblich, warm" },
        shimmer: { label: "Shimmer", tone: "Weiblich, hell" },
        coral: { label: "Coral", tone: "Weiblich, ausdrucksstark" },
        sage: { label: "Sage", tone: "Weiblich, ruhig" },
        fable: { label: "Fable", tone: "Erzählend, britisch" },
        onyx: { label: "Onyx", tone: "Männlich, tief" },
        echo: { label: "Echo", tone: "Männlich, ruhig" },
        ash: { label: "Ash", tone: "Männlich, jung" },
        ballad: { label: "Ballad", tone: "Männlich, sanft" },
        verse: { label: "Verse", tone: "Männlich, ausdrucksstark" },
      },
      player: {
        tracksBadge: "{{count}} Titel",
        voiceALabel: "Stimme A (Moderator)",
        voiceBLabel: "Stimme B (Experte)",
        speedLabel: "Geschwindigkeit: {{speed}}×",
        listen: "Anhören",
        generatingVoice: "Stimme wird erzeugt…",
        resume: "Fortsetzen",
        pause: "Pause",
        stopAria: "Stopp",
        chaptersLabel: "Kapitel",
        chapterIntro: "Einleitung",
        chapterOutro: "Schluss",
        chapterPart: "Teil {{n}}",
        hostLabel: "Moderator",
        expertLabel: "Experte",
        albumLabel: "Lumen · Podcast",
        ttsInstructions: "Sprich auf Deutsch mit einem natürlichen, warmen und mitreißenden Ton, wie ein echter Podcaster.",
      },
    },
    errors: {
      cannotReadFile: "Die Datei kann nicht gelesen werden",
      noMapToEdit: "Keine Mindmap zum Bearbeiten",
      textTooLarge: "Die Textdatei ist zu groß (max. 5 MB)",
      audioTooLarge: "Die Audiodatei ist zu groß (max. 20 MB)",
      imageTooLarge: "Das Bild ist zu groß (max. 10 MB)",
      missingTitleCenter: "Füge einen Titel und eine zentrale Idee hinzu",
    },
    map: {
      tabs: { ai: "Mit KI erstellen", manual: "Manuell erstellen", library: "Meine Mindmaps" },
      library: { title: "Meine Mindmaps", itemLabel: "Mindmap" },
      ai: {
        title: "Erstelle eine Mindmap mit KI",
        desc: "Die KI analysiert das Thema und erstellt automatisch eine vollständige, hierarchische Mindmap.",
        sourceLabel: "Quelle",
        source: { topic: "Thema", text: "Text", audio: "Audio", image: "Foto" },
        text: {
          placeholder: "Füge hier deine Notizen, ein Kapitel oder eine Zusammenfassung ein…",
          uploadFile: ".txt-/.md-Datei hochladen",
          charsCount: "{{count}}/20000",
        },
        audio: {
          transcribing: "Transkription läuft…",
          uploadLabel: "Aufnahme hochladen",
          formats: "MP3, WAV, M4A, WEBM · max. 20 MB",
        },
        image: {
          reading: "Bild wird gelesen…",
          uploadLabel: "Foto hochladen",
          formats: "Notizen, Buchseite, Tafel · max. 10 MB",
        },
        fromLabel: "Von: {{name}}",
        topicLabel: "Thema",
        mapTitleLabel: "Titel der Mindmap",
        topicPlaceholder: "Z. B. Der Zweite Weltkrieg",
        mapTitlePlaceholder: "Z. B. Geschichtsnotizen",
        typeLabel: "Art der Mindmap",
        kind: {
          mind: "Mindmap (radial)",
          concept: "Concept Map (Novak)",
          cornell: "Cornell-Schema",
          dsa: "Barrierearme Prüfungsmap",
        },
        dsaNote:
          "Mindmap für Schüler mit Lernschwierigkeiten konzipiert: kurze Beschriftungen, visuelle Symbole, nur das Wesentliche für die Prüfung, mit einer Beispielfrage pro Ast.",
        customRequestLabel: "Spezifische Wünsche",
        optional: "(optional)",
        customRequestPlaceholderDsa: "Z. B. konzentriere dich auf Kapitel 3 und 4, hebe Daten hervor, verwende Farben für Personen",
        customRequestPlaceholderDefault: "Z. B. füge praktische Beispiele hinzu, vereinfache die Sprache",
        generateButton: "Mindmap erstellen",
        generatingButton: "Mindmap wird erstellt…",
        dsaBadge: "Barrierearm · Prüfung",
        editRequestButton: "Mit Anfrage bearbeiten",
        refine: {
          title: "Mindmap bearbeiten",
          desc: "Schreibe, was du ändern möchtest: Die KI aktualisiert die Mindmap und behält die bestehende Struktur bei.",
          placeholder: "Z. B. füge einen Ast zu den Folgen hinzu, kürze die Beschriftungen, füge mehr Daten hinzu, entferne den Ast zu den Personen",
          defaultPrompt: "Verbessere Klarheit und Einfachheit.",
          updateButton: "Mindmap aktualisieren",
          updatingButton: "Wird aktualisiert…",
          suggestions: {
            simplify: "Sprache vereinfachen",
            addDates: "Mehr Daten/Zahlen hinzufügen",
            shorterLabels: "Kürzere Beschriftungen",
            moreVisualSymbols: "Mehr visuelle Symbole",
            essentialOnly: "Nur das Wesentliche für die Prüfung",
          },
        },
        toasts: {
          mapGenerated: "Mindmap erstellt ✨",
          mapUpdated: "Mindmap aktualisiert ✨",
          textUploaded: "Text hochgeladen",
          audioTranscribed: "Audio transkribiert ✨",
          imageExtracted: "Text aus Bild extrahiert ✨",
        },
      },
      manual: {
        titleLabel: "Titel der Mindmap",
        titlePlaceholder: "Z. B. Geschichtswiederholung",
        centerLabel: "Zentrale Idee",
        centerPlaceholder: "Z. B. Französische Revolution",
        defaultCenter: "Zentrale Idee",
        branchPlaceholder: "Hauptast",
        removeBranchAria: "Ast entfernen",
        subPlaceholder: "Unterthema {{n}}",
        addSubButton: "Hinzufügen",
        addBranchButton: "Ast hinzufügen",
        saveButton: "Mindmap speichern",
        savingButton: "Wird gespeichert…",
        previewLabel: "Vorschau",
        toastSaved: "Mindmap gespeichert ✨",
      },
    },
    guide: {
      whyTitle: "Warum Mindmaps erstellen?",
      whenLabel: "Wann verwenden:",
      whyDesc:
        "Mindmaps übersetzen Text in Bilder. Das Gehirn erinnert sich an 80% dessen, was es sieht und räumlich organisiert, gegenüber 20% dessen, was es linear liest. Sie sind das Werkzeug Nr. 1 für aktives Wiederholen.",
      mindMap: {
        kind: "Mindmap",
        pillars: [
          "1 zentrale Idee in der Mitte (Wort + Bild).",
          "3-6 Hauptäste, die strahlenförmig ausgehen.",
          "Jeder Ast öffnet sich in dünnere Unteräste.",
          "Eine andere Farbe für jeden Ast (hilft dem Gedächtnis).",
          "Schlüsselwörter, keine langen Sätze.",
        ],
        when: "Für Brainstorming, kreatives Wiederholen, offene Themen (Aufsätze, Geschichte, Themen).",
        example: "BEISPIEL — \"Französische Revolution\"\n\n            Ursachen\n              |\nGesellschaft — REVOLUTION — Phasen\n              |\n         Folgen",
      },
      conceptMap: {
        kind: "Concept Map (Novak)",
        pillars: [
          "Konzepte in Kästen, Hierarchie von oben nach unten.",
          "Pfeile, die Konzepte mit einer Beziehungsbezeichnung verbinden (z. B. 'verursacht', 'besteht aus').",
          "Sollte wie Sätze gelesen werden: Subjekt → Verb → Objekt.",
          "Geeignet, um Prozesse und Ursache-Wirkungs-Beziehungen zu erklären.",
        ],
        when: "Für naturwissenschaftliche, technische Fächer oder wenn du Beziehungen zwischen Konzepten zeigen musst.",
        example: "[Pflanzen]\n   |bestehen aus\n[Stängel] — [Blätter] — [Wurzeln]\n              |erzeugen\n         [Photosynthese]",
      },
      cornell: {
        kind: "Cornell-Schema",
        pillars: [
          "Teile die Seite in 3 Zonen ein.",
          "Rechte Spalte (breit): im Unterricht gemachte Notizen.",
          "Linke Spalte (schmal): Schlüsselwörter und Fragen.",
          "Unterer Streifen: nach dem Unterricht aus dem Gedächtnis geschriebene Zusammenfassung.",
        ],
        when: "Während Vorlesungen, Videokursen, Lehrbuchlektüren.",
        example: "| Wörter    | Notizen…                        |\n| Schlüssel | Notizen…                        |\n| Fragen?   | Notizen…                        |\n|-----------|---------------------------------|\n| Zusammenfassung: 3 Zeilen aus dem Gedächtnis am Ende des Unterrichts. |",
      },
      method: {
        title: "Methode in 5 Schritten",
        steps: [
          { title: "Aktiv lesen", desc: "Lies zuerst das ganze Kapitel, ohne etwas zu schreiben. Unterstreiche nur die Schlüsselwörter." },
          { title: "Die zentrale Idee finden", desc: "Fasse sie in 1-3 Wörtern zusammen. Sie ist das Herz der Mindmap." },
          { title: "Die Äste identifizieren", desc: "Suche 3-6 Hauptthemen, die die Frage \"worum geht es?\" beantworten." },
          { title: "Details hinzufügen", desc: "Schreibe für jeden Ast 2-4 Unterthemen — nur Schlüsselwörter." },
          { title: "Mit geschlossenen Augen wiederholen", desc: "Schließe die Mindmap und versuche, sie aus dem Gedächtnis nachzubilden. Fehlt etwas, gehe zu diesem Ast zurück." },
        ],
      },
    },
  },
  pt: {
    meta: {
      title: "Podcasts e Mapas — Lumen",
      description:
        "Transforma qualquer assunto num podcast de estudo ou num mapa mental. Com um guia integrado sobre como construir mapas eficazes.",
    },
    page: {
      kicker: "Ferramentas",
      title: "Podcasts e Mapas",
      subtitle:
        "Ouve os teus temas como um podcast ou visualiza-os como mapas. Aprende mais depressa ativando mais sentidos.",
    },
    tabs: { podcast: "Podcast", maps: "Mapas", guide: "Guia" },
    podcast: {
      card: {
        title: "Gera um podcast de estudo",
        desc: "Uma conversa entre duas vozes que explica o tema como se estivesses a ouvir rádio.",
      },
      topicLabel: "Tema",
      topicPlaceholder: "Ex. A Revolução Francesa, O ciclo de Krebs, As equações diferenciais…",
      levelLabel: "Nível",
      level: { base: "Básico", medio: "Médio", avanzato: "Avançado" },
      durationLabel: "Duração: {{min}} min",
      sourceLabel: "Material de partida (opcional)",
      sourcePlaceholder: "Cola apontamentos, ou anexa um áudio (aula gravada) ou uma foto (página do livro).",
      generateButton: "Gerar podcast",
      generatingButton: "A escrever o podcast…",
      toastReady: "Podcast pronto ✨",
      historyTitle: "Os teus podcasts",
      historyBadge: "{{min}}′ · {{level}}",
      voices: {
        alloy: { label: "Alloy", tone: "Neutra, versátil" },
        nova: { label: "Nova", tone: "Feminina, calorosa" },
        shimmer: { label: "Shimmer", tone: "Feminina, clara" },
        coral: { label: "Coral", tone: "Feminina, expressiva" },
        sage: { label: "Sage", tone: "Feminina, serena" },
        fable: { label: "Fable", tone: "Narrativa, britânica" },
        onyx: { label: "Onyx", tone: "Masculina, profunda" },
        echo: { label: "Echo", tone: "Masculina, calma" },
        ash: { label: "Ash", tone: "Masculina, jovem" },
        ballad: { label: "Ballad", tone: "Masculina, suave" },
        verse: { label: "Verse", tone: "Masculina, expressiva" },
      },
      player: {
        tracksBadge: "{{count}} faixas",
        voiceALabel: "Voz A (apresentador)",
        voiceBLabel: "Voz B (especialista)",
        speedLabel: "Velocidade: {{speed}}×",
        listen: "Ouvir",
        generatingVoice: "A gerar a voz…",
        resume: "Retomar",
        pause: "Pausa",
        stopAria: "Parar",
        chaptersLabel: "Capítulos",
        chapterIntro: "Introdução",
        chapterOutro: "Conclusão",
        chapterPart: "Parte {{n}}",
        hostLabel: "Apresentador",
        expertLabel: "Especialista",
        albumLabel: "Lumen · Podcast",
        ttsInstructions: "Fala em português com um tom natural, caloroso e envolvente, como um verdadeiro podcaster.",
      },
    },
    errors: {
      cannotReadFile: "Não é possível ler o ficheiro",
      noMapToEdit: "Nenhum mapa para editar",
      textTooLarge: "O ficheiro de texto é demasiado grande (máx. 5MB)",
      audioTooLarge: "O ficheiro de áudio é demasiado grande (máx. 20MB)",
      imageTooLarge: "A imagem é demasiado grande (máx. 10MB)",
      missingTitleCenter: "Adiciona um título e uma ideia central",
    },
    map: {
      tabs: { ai: "Gerar com IA", manual: "Criar manualmente", library: "Os meus mapas" },
      library: { title: "Os meus mapas", itemLabel: "mapa" },
      ai: {
        title: "Gera um mapa com IA",
        desc: "A IA analisa o tema e constrói automaticamente um mapa completo e hierárquico.",
        sourceLabel: "Fonte",
        source: { topic: "Tema", text: "Texto", audio: "Áudio", image: "Foto" },
        text: {
          placeholder: "Cola aqui os teus apontamentos, um capítulo, um resumo…",
          uploadFile: "Carregar ficheiro .txt / .md",
          charsCount: "{{count}}/20000",
        },
        audio: {
          transcribing: "A transcrever…",
          uploadLabel: "Carregar uma gravação",
          formats: "MP3, WAV, M4A, WEBM · máx. 20MB",
        },
        image: {
          reading: "A ler a imagem…",
          uploadLabel: "Carregar uma foto",
          formats: "Apontamentos, página de livro, quadro · máx. 10MB",
        },
        fromLabel: "De: {{name}}",
        topicLabel: "Tema",
        mapTitleLabel: "Título do mapa",
        topicPlaceholder: "Ex. A Segunda Guerra Mundial",
        mapTitlePlaceholder: "Ex. Apontamentos de história",
        typeLabel: "Tipo de mapa",
        kind: {
          mind: "Mapa mental (radial)",
          concept: "Mapa concetual (Novak)",
          cornell: "Esquema Cornell",
          dsa: "Mapa acessível para o exame",
        },
        dsaNote:
          "Mapa pensado para alunos com dificuldades de aprendizagem: etiquetas curtas, símbolos visuais, apenas o essencial para o exame, com uma pergunta modelo por cada ramo.",
        customRequestLabel: "Pedidos específicos",
        optional: "(opcional)",
        customRequestPlaceholderDsa: "Ex. concentra-te nos capítulos 3 e 4, destaca as datas, usa cores para as personagens",
        customRequestPlaceholderDefault: "Ex. adiciona exemplos práticos, simplifica a linguagem",
        generateButton: "Gerar mapa",
        generatingButton: "A construir o mapa…",
        dsaBadge: "Acessível · exame",
        editRequestButton: "Editar com um pedido",
        refine: {
          title: "Editar o mapa",
          desc: "Escreve o que queres alterar: a IA atualiza o mapa mantendo a estrutura existente.",
          placeholder: "Ex. adiciona um ramo sobre as consequências, encurta as etiquetas, adiciona mais datas, remove o ramo sobre as personagens",
          defaultPrompt: "Melhora a clareza e a simplicidade.",
          updateButton: "Atualizar mapa",
          updatingButton: "A atualizar…",
          suggestions: {
            simplify: "Simplifica a linguagem",
            addDates: "Adiciona mais datas/números",
            shorterLabels: "Etiquetas mais curtas",
            moreVisualSymbols: "Mais símbolos visuais",
            essentialOnly: "Apenas o essencial para o exame",
          },
        },
        toasts: {
          mapGenerated: "Mapa gerado ✨",
          mapUpdated: "Mapa atualizado ✨",
          textUploaded: "Texto carregado",
          audioTranscribed: "Áudio transcrito ✨",
          imageExtracted: "Texto extraído da imagem ✨",
        },
      },
      manual: {
        titleLabel: "Título do mapa",
        titlePlaceholder: "Ex. Revisão de história",
        centerLabel: "Ideia central",
        centerPlaceholder: "Ex. Revolução Francesa",
        defaultCenter: "Ideia central",
        branchPlaceholder: "Ramo principal",
        removeBranchAria: "Remover ramo",
        subPlaceholder: "Subtema {{n}}",
        addSubButton: "Adicionar",
        addBranchButton: "Adicionar ramo",
        saveButton: "Guardar mapa",
        savingButton: "A guardar…",
        previewLabel: "Pré-visualização",
        toastSaved: "Mapa guardado ✨",
      },
    },
    guide: {
      whyTitle: "Porquê fazer mapas?",
      whenLabel: "Quando usar:",
      whyDesc:
        "Os mapas traduzem o texto em imagens. O cérebro recorda 80% do que vê e organiza espacialmente, contra 20% do que lê de forma linear. São a ferramenta n.º 1 para a revisão ativa.",
      mindMap: {
        kind: "Mapa mental",
        pillars: [
          "1 ideia central ao centro (palavra + imagem).",
          "3-6 ramos principais que partem em raio.",
          "Cada ramo abre-se em sub-ramos mais finos.",
          "Uma cor diferente para cada ramo (ajuda a memória).",
          "Palavras-chave, não frases longas.",
        ],
        when: "Para brainstorming, revisão criativa, temas abertos (ensaios, história, temas).",
        example: "EXEMPLO — \"Revolução francesa\"\n\n            Causas\n              |\nSociedade — REVOLUÇÃO — Fases\n              |\n         Consequências",
      },
      conceptMap: {
        kind: "Mapa concetual (Novak)",
        pillars: [
          "Conceitos em caixas, hierarquia de cima para baixo.",
          "Setas que ligam conceitos com uma etiqueta de relação (ex. 'causa', 'é formado por').",
          "Deve ler-se como frases: sujeito → verbo → objeto.",
          "Adequado para explicar processos e relações causa-efeito.",
        ],
        when: "Para disciplinas científicas, técnicas, ou quando precisas de mostrar relações entre conceitos.",
        example: "[Plantas]\n   |é formada por\n[Caule] — [Folhas] — [Raízes]\n              |produzem\n         [Fotossíntese]",
      },
      cornell: {
        kind: "Esquema Cornell",
        pillars: [
          "Divide a página em 3 zonas.",
          "Coluna direita (larga): apontamentos tirados na aula.",
          "Coluna esquerda (estreita): palavras-chave e perguntas.",
          "Faixa inferior: resumo escrito de memória depois da aula.",
        ],
        when: "Durante aulas expositivas, videoaulas, leituras de manuais.",
        example: "| Palavras  | Apontamentos…                   |\n| chave     | Apontamentos…                   |\n| perguntas?| Apontamentos…                   |\n|-----------|---------------------------------|\n| Resumo: 3 linhas de memória no fim da aula. |",
      },
      method: {
        title: "Método em 5 passos",
        steps: [
          { title: "Lê ativamente", desc: "Primeiro lê todo o capítulo sem escrever nada. Sublinha apenas as palavras-chave." },
          { title: "Encontra a ideia central", desc: "Resume-a em 1-3 palavras. É o coração do mapa." },
          { title: "Identifica os ramos", desc: "Procura 3-6 macrotemas que respondam a \"do que trata?\"." },
          { title: "Adiciona detalhes", desc: "Para cada ramo escreve 2-4 subtemas — apenas palavras-chave." },
          { title: "Revê de olhos fechados", desc: "Fecha o mapa e tenta refazê-lo de memória. Se faltar algo, volta a esse ramo." },
        ],
      },
    },
  },
};
